import type { OpenAPI } from "simplydo/interfaces";

import { pluralise } from "./helpers";

export type SubmissibilityDetail = NonNullable<
  NonNullable<
    OpenAPI.Schemas["ideaSubmissibilityStatus"]["submissibilityStatus"]
  >["detail"]
>[string];

export type SubmissibilityIncorrectField = NonNullable<
  SubmissibilityDetail["incorrectFields"]
>[number];

export const SUBMISSIBILITY_COLORS = {
  complete: "#28a745",
  incomplete: "#007bff",
  error: "#dc3545",
  background: "#ccc",
} as const;

const errorMessages = {
  required: "Please enter a value",
  wordLimit: "You are currently exceeding the word limit",
};

const requiredMessages: Record<string, string> = {
  dropdown: "Please select a value",
  optionMultiple: "Please select an option",
  image: "Please select an image",
  video: "Please upload a video",
  file: "Please upload a file",
  multiimage: "Please upload an image",
  multifile: "Please upload a file",
  slider: "Please enter a value",
  creditsafereport: "Please select a value",
};

export const getSubmissibilitySectionKey = (
  section: NonNullable<OpenAPI.Schemas["IdeaTemplate"]["body"]>[number],
) => {
  if (section.type === "impact") {
    return section.id || "impactStatement";
  }
  return section.id;
};

export const getSectionSubmissibility = (
  idea:
    | OpenAPI.Assign<
        OpenAPI.Schemas["Idea"],
        OpenAPI.Schemas["AssignIdeaSubmissibilityStatus"]
      >
    | undefined,
  section: NonNullable<OpenAPI.Schemas["IdeaTemplate"]["body"]>[number],
) => {
  const sectionKey = getSubmissibilitySectionKey(section);
  if (!sectionKey) {
    return undefined;
  }
  return idea?.submissibilityStatus?.detail?.[sectionKey];
};

export const getSubmissibilityProgress = (
  submissibility: SubmissibilityDetail | undefined,
) => {
  if (!submissibility?.visibleFields) {
    return 0;
  }
  return submissibility.completedFields / submissibility.visibleFields;
};

export const getSubmissibilityColor = (
  submissibility: SubmissibilityDetail | undefined,
) => {
  if (!submissibility) {
    return undefined;
  }
  if (submissibility.completedFields === submissibility.visibleFields) {
    return SUBMISSIBILITY_COLORS.complete;
  }
  if ((submissibility.incorrectFields?.length || 0) > 0) {
    return SUBMISSIBILITY_COLORS.error;
  }
  return SUBMISSIBILITY_COLORS.incomplete;
};

export const isSubmissibilitySectionComplete = (
  submissibility: SubmissibilityDetail | undefined,
) => {
  if (!submissibility) {
    return false;
  }
  return submissibility.completedFields === submissibility.visibleFields;
};

export const groupIncorrectFields = (
  submissibility: SubmissibilityDetail | undefined,
) => {
  const incorrectFields = submissibility?.incorrectFields || [];
  return {
    required: incorrectFields.filter((field) => field.type === "required"),
    wordLimit: incorrectFields.filter((field) => field.type === "wordLimit"),
    custom: incorrectFields.filter((field) => field.type === "custom"),
    error: incorrectFields.filter((field) => field.type === "error"),
  };
};

export const getSubmissibilitySummaryText = (
  submissibility: SubmissibilityDetail | undefined,
) => {
  if (!submissibility) {
    return "";
  }

  const { required, wordLimit, custom, error } =
    groupIncorrectFields(submissibility);

  const optionalFields =
    (submissibility.visibleFields || 0) - (submissibility.requiredFields || 0);
  const optionalFieldsCompleted =
    (submissibility.completedFields || 0) -
    (submissibility.completedRequiredFields || 0);
  const remainingOptionalFields = optionalFields - optionalFieldsCompleted;
  const totalRemaining =
    (submissibility.incorrectFields?.length || 0) + remainingOptionalFields;

  let summary = "";
  if (required.length > 0) {
    summary = `${required.length} required`;
  }
  if (remainingOptionalFields > 0) {
    summary =
      summary ?
        `${summary} (${remainingOptionalFields} optional)`
      : `${remainingOptionalFields} optional`;
  }
  if (summary) {
    return `${summary} ${pluralise(totalRemaining, "field", "fields", false)} remaining`;
  }
  if (wordLimit.length > 0 || custom.length > 0 || error.length > 0) {
    return "Issues need to be resolved";
  }
  return "All fields completed!";
};

export const getIncorrectFieldErrorMessage = (
  incorrectField: SubmissibilityIncorrectField | undefined,
  fieldType?: string,
) => {
  if (!incorrectField?.type) {
    return null;
  }
  if (incorrectField.type === "required") {
    return (fieldType && requiredMessages[fieldType]) || errorMessages.required;
  }
  if (incorrectField.type === "wordLimit") {
    return errorMessages.wordLimit;
  }
  return incorrectField.description || errorMessages.required;
};

export const getFieldWordCount = (
  idea:
    | OpenAPI.Assign<
        OpenAPI.Schemas["Idea"],
        OpenAPI.Schemas["AssignIdeaSubmissibilityStatus"]
      >
    | undefined,
  fieldId: string,
) => {
  return (
    idea?.submissibilityStatus?.wordCounts?.find((w) => w.field === fieldId)
      ?.count || 0
  );
};
